// src/App.jsx - COMPLETE VERSION WITH THEME TOGGLE SUPPORT
// Version 6.2.0 - Fixed missing component imports (FIASMonitor, SystemMonitoring removed)

import React, { useEffect, Suspense, useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography, Container, Button } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import debounce from 'lodash/debounce';
import toast from 'react-hot-toast';

//  Store and hook imports
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { checkAuth } from './store/slices/authSlice';
import { forceLogoutToLogin } from './services/sessionManager';

//  THEME IMPORTS - Both light and dark
import { lightTheme, darkTheme } from './theme';

//  ONLY IMPORT EXISTING COMPONENTS
import Layout from './components/common/Layout.jsx';
import ProtectedRoute from './components/common/ProtectedRoute.jsx';

//  Socket initialization without top-level await
let connectSocket = null;
let getSocket = null;

const initializeSocket = async () => {
  try {
    const socketModule = await import('./services/socket');
    connectSocket = socketModule.connectSocket || socketModule.default?.connectSocket;
    getSocket = socketModule.getSocket || socketModule.default?.getSocket;
    debugLog(' Socket module loaded successfully');
    return true;
  } catch (error) {
    console.warn(' Socket service not available:', error.message);
    connectSocket = () => null;
    getSocket = () => null;
    return false;
  }
};
const Login = React.lazy(() => import('./pages/Login.jsx'));
const Register = React.lazy(() => import('./pages/Register.jsx'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword.jsx'));
const Dashboard = React.lazy(() => import('./pages/Dashboard.jsx'));
const Devices = React.lazy(() => import('./pages/Devices.jsx'));
const Rooms = React.lazy(() => import('./pages/Rooms.jsx'));
const Users = React.lazy(() => import('./pages/Users.jsx'));
const Vouchers = React.lazy(() => import('./pages/Vouchers.jsx'));
const Logs = React.lazy(() => import('./pages/Logs.jsx'));
const Settings = React.lazy(() => import('./pages/Settings.jsx'));

const Branches = React.lazy(() => import('./pages/Branches.jsx'));
const AccessTokens = React.lazy(() => import('./pages/AccessTokens.jsx'));
const SuperAdminConsole = React.lazy(() => import('./pages/SuperAdminConsole.jsx'));
const DynamicSlotsPage = React.lazy(() => import('./pages/DynamicSlotsPage.jsx'));
const Support = React.lazy(() => import('./pages/Support.jsx'));

const ReactQueryDevtools = import.meta.env.DEV
  ? React.lazy(() =>
      import('@tanstack/react-query-devtools').then((module) => ({
        default: module.ReactQueryDevtools,
      }))
    )
  : null;

const isDev = import.meta.env.DEV;
const debugLog = (...args) => {
  if (isDev) {
    console.log(...args);
  }
};

//  REMOVED: Non-existent components
// const FIASMonitor = React.lazy(() => import('./pages/FIASMonitor.jsx'));
// const SystemMonitoring = React.lazy(() => import('./pages/SystemMonitoring.jsx'));

//  Enhanced Loading Component
const LoadingFallback = ({ message = "Loading..." }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      gap: 2
    }}>
    <CircularProgress size={60} thickness={4} />
    <Typography variant="h6" color="primary" sx={{
      fontWeight: "medium"
    }}>
      {message}
    </Typography>
    <Typography variant="body2" sx={{
      color: "text.secondary"
    }}>
      Please wait while the component loads...
    </Typography>
  </Box>
);

//  NotFound Component
const NotFound = () => (
  <Container maxWidth="sm">
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        textAlign: "center",
        gap: 2
      }}>
      <Typography variant="h1" color="primary" sx={{
        fontWeight: "bold"
      }}>
        404
      </Typography>
      <Typography variant="h4" gutterBottom>
        Page Not Found
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          mb: 3
        }}>
        The page you're looking for doesn't exist.
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 2
        }}>
        <Button
          variant="contained"
          onClick={() => (window.location.href = '/dashboard')}
        >
          Go to Dashboard
        </Button>
        <Button
          variant="outlined"
          onClick={() => window.history.back()}
        >
          Go Back
        </Button>
      </Box>
    </Box>
  </Container>
);

//  Query Client Configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error?.response?.status === 401) return false;
        if (error?.response?.status >= 400 && error?.response?.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchInterval: (data, query) => {
        // Auto-refresh for monitoring pages
        if (query.queryKey.includes('devices') || query.queryKey.includes('monitoring')) {
          return 60000; // 1 minute
        }
        return false;
      },
    },
    mutations: {
      retry: 1,
      onError: (error) => {
        console.error('Mutation error:', error);
        if (error?.response?.status === 401) {
          void forceLogoutToLogin({
            onBeforeRedirect: async () => {
              try {
                const { disconnectSocket } = await import('./services/socket');
                disconnectSocket();
              } catch (socketError) {
                console.warn('Could not disconnect socket during mutation auth failure:', socketError);
              }
            }
          });
        }
      },
    },
  },
});

// Make queryClient available globally for socket handlers
if (typeof window !== 'undefined') {
  window.queryClient = queryClient;
}

//  Protected Route Component
const BasicProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAppSelector(state => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

//  Main App Content Component with Theme Toggle
function AppContent() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, loading, accessToken, user, selectedBranch } = useAppSelector((state) => state.auth);
  const [socketInitialized, setSocketInitialized] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  //  THEME STATE MANAGEMENT - Default to light (white) theme
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem('themeMode') || 'light'; // Default to light
  });

  //  TOGGLE THEME FUNCTION
  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
    localStorage.setItem('themeMode', newMode);
    debugLog('🎨 Theme switched to:', newMode);
  };

  //  SELECT THEME BASED ON MODE
  const currentTheme = useMemo(() => {
    return themeMode === 'dark' ? darkTheme : lightTheme;
  }, [themeMode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  //  Debug logging for troubleshooting
  useEffect(() => {
    if (!isDev) return;

    debugLog(' App State Debug:', {
      isAuthenticated,
      loading,
      hasUser: !!user,
      hasToken: !!accessToken,
      hasBranch: !!selectedBranch,
      userRole: user?.role,
      branchName: selectedBranch?.name,
      socketInitialized,
      currentPath: window.location.pathname,
      themeMode,
    });
  }, [isAuthenticated, loading, user, accessToken, selectedBranch, socketInitialized, themeMode]);

  //  Background auth verification
  // Store is already hydrated from localStorage — this runs silently in background
  // to validate token freshness. On failure it clears state and redirects to login.
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token && !user) {
      // Fire-and-forget: don't await, don't block rendering
      dispatch(checkAuth()).catch(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('selectedBranch');
      });
    }
  }, [dispatch, user]);

  //  Socket initialization
  useEffect(() => {
    if (!isAuthenticated || !accessToken || socketInitialized) {
      return;
    }

    let cancelled = false;
    let idleId;
    let timeoutId;

    const initSocket = async () => {
      if (cancelled) return;

      debugLog(' Initializing socket...');
      const success = await initializeSocket();

      if (!cancelled) {
        setSocketInitialized(success);
        if (success) {
          debugLog(' Socket initialization successful');
        }
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(() => {
        void initSocket();
      }, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(() => {
        void initSocket();
      }, 350);
    }

    return () => {
      cancelled = true;
      if (idleId && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isAuthenticated, accessToken, socketInitialized]);

  //  Socket connection management WITH FIXED TOAST HANDLERS
  useEffect(() => {
    if (hasUserInteracted) return;

    const markInteracted = () => setHasUserInteracted(true);

    window.addEventListener('pointerdown', markInteracted, { once: true, passive: true });
    window.addEventListener('keydown', markInteracted, { once: true });
    window.addEventListener('touchstart', markInteracted, { once: true, passive: true });
    window.addEventListener('scroll', markInteracted, { once: true, passive: true });

    return () => {
      window.removeEventListener('pointerdown', markInteracted);
      window.removeEventListener('keydown', markInteracted);
      window.removeEventListener('touchstart', markInteracted);
      window.removeEventListener('scroll', markInteracted);
    };
  }, [hasUserInteracted]);

  //  Socket connection management WITH deferred notifications
  useEffect(() => {
    if (isAuthenticated && accessToken && user && socketInitialized && connectSocket) {
      debugLog(' Establishing socket connection...');
      const shouldNotify = hasUserInteracted;

      try {
        const socket = connectSocket(accessToken);

        if (socket) {
          // Debounced query invalidation
          const debouncedDeviceInvalidate = debounce(() => {
            queryClient.invalidateQueries({ queryKey: ['devices'] });
            queryClient.invalidateQueries({ queryKey: ['device-counts'] });
          }, 2000);

          const debouncedIntegrationInvalidate = debounce(() => {
            queryClient.invalidateQueries({ queryKey: ['integration-status'] });
            queryClient.invalidateQueries({ queryKey: ['settings-status'] });
          }, 3000);

          const debouncedRoomInvalidate = debounce(() => {
            queryClient.invalidateQueries({ queryKey: ['rooms'] });
          }, 2500);

          // ===================================
          //  SOCKET EVENT HANDLERS - FIXED
          // ===================================

          socket.on('connect', () => {
            debugLog(' Socket connected successfully');
            if (shouldNotify) {
              toast.success('Connected to real-time updates', {
                duration: 2000,
                icon: ''
              });
            }
          });

          socket.on('disconnect', (reason) => {
            debugLog(' Socket disconnected:', reason);
            if (shouldNotify && reason !== 'io client disconnect') {
              toast.error('Lost connection to server', {
                duration: 3000,
                icon: ''
              });
            }
          });

          socket.on('device_status_changed', (data) => {
            debugLog(' Device status changed:', data);
            debouncedDeviceInvalidate();

            const deviceName = data.name || data.deviceName || data.deviceId || 'Device';
            const status = data.currentStatus || data.status || 'unknown';

            if (shouldNotify) {
              toast.success(`${deviceName} is now ${status}`, {
                duration: 3000,
                icon: status === 'online' ? '' : status === 'error' ? '🔴' : '',
              });
            }
          });

          socket.on('integration_status_changed', (data) => {
            debugLog(' Integration status changed:', data);
            debouncedIntegrationInvalidate();

            const integrationName = data.integration?.toUpperCase() || 'Integration';
            const status = data.enabled ? 'enabled' : 'disabled';

            if (shouldNotify) {
              toast.success(`${integrationName} has been ${status}`, {
                duration: 4000,
                icon: data.enabled ? '' : '🔴',
              });
            }
          });

          socket.on('room_status_update', (data) => {
            debugLog(' Room status updated:', data);
            debouncedRoomInvalidate();

            if (data.roomNumber) {
              if (shouldNotify) {
                toast.success(`Room ${data.roomNumber} status updated`, {
                  duration: 3000,
                  icon: '',
                });
              }
            }
          });

          socket.on('device:initialized', (data) => {
            debugLog(' Device initialized:', data);
            debouncedDeviceInvalidate();

            if (shouldNotify) {
              toast.success(`${data.name} is initializing...`, {
                duration: 3000,
                icon: '',
              });
            }
          });

          socket.on('device:error', (data) => {
            debugLog(' Device error:', data);
            debouncedDeviceInvalidate();

            if (shouldNotify) {
              toast.error(`${data.name}: ${data.error}`, {
                duration: 5000,
                icon: '',
              });
            }
          });

          socket.on('device:deactivated', (data) => {
            debugLog(' Device deactivated:', data);
            debouncedDeviceInvalidate();

            if (shouldNotify) {
              toast.success(`${data.name} has been deactivated`, {
                duration: 3000,
                icon: '🔴',
              });
            }
          });

          socket.on('device:deleted', (data) => {
            debugLog(' Device deleted:', data);
            debouncedDeviceInvalidate();

            if (shouldNotify) {
              toast.success(`${data.name} has been deleted`, {
                duration: 3000,
                icon: '',
              });
            }
          });

          socket.on('fias_message', (data) => {
            debugLog('📨 FIAS message received:', data);
            if (data.message?.fields?.RN) {
              debouncedRoomInvalidate();
            }
          });

          socket.on('error', (error) => {
            console.error(' Socket error:', error);
            if (shouldNotify) {
              toast.error('Connection error occurred', {
                duration: 5000,
                icon: ''
              });
            }
          });

          // Cleanup function
          return () => {
            debugLog(' Cleaning up socket listeners');
            debouncedDeviceInvalidate.cancel();
            debouncedIntegrationInvalidate.cancel();
            debouncedRoomInvalidate.cancel();

            socket.off('connect');
            socket.off('disconnect');
            socket.off('device_status_changed');
            socket.off('integration_status_changed');
            socket.off('room_status_update');
            socket.off('device:initialized');
            socket.off('device:error');
            socket.off('device:deactivated');
            socket.off('device:deleted');
            socket.off('fias_message');
            socket.off('error');
          };
        }
      } catch (error) {
        console.warn(' Socket connection failed:', error);
        if (shouldNotify) {
          toast.error('Real-time updates unavailable', {
            duration: 3000
          });
        }
      }
    }
  }, [isAuthenticated, accessToken, user, socketInitialized, hasUserInteracted]);

  //  Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2
        }}>
        <CircularProgress size={80} thickness={4} />
        <Typography variant="h5" color="primary" sx={{
          fontWeight: "medium"
        }}>
          Initializing Hospitality Middleware...
        </Typography>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Loading authentication and system components
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Router>
        <Suspense fallback={<LoadingFallback message="Loading page..." />}>
          <Routes>
            {/*  Public Routes */}
            <Route
              path="/login"
              element={<Login />}
            />
            
            <Route
              path="/register"
              element={<Register />}
            />

            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />

            {/*  Protected Routes - Pass theme toggle to Layout */}
            <Route
              path="/"
              element={
                <BasicProtectedRoute>
                  <Layout themeMode={themeMode} toggleTheme={toggleTheme} />
                </BasicProtectedRoute>
              }
            >
              <Route
                path="dashboard"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading Dashboard..." />}>
                    <Dashboard />
                  </Suspense>
                }
              />

              <Route
                path="rooms"
                element={
                  <ProtectedRoute requiredPage="rooms">
                    <Suspense fallback={<LoadingFallback message="Loading Rooms..." />}>
                      <Rooms />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="devices"
                element={
                  <ProtectedRoute requiredPage="devices">
                    <Suspense fallback={<LoadingFallback message="Loading Devices..." />}>
                      <Devices />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="vouchers"
                element={
                  <ProtectedRoute requiredPage="vouchers">
                    <Suspense fallback={<LoadingFallback message="Loading Vouchers..." />}>
                      <Vouchers />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="users"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading Users..." />}>
                    <Users />
                  </Suspense>
                }
              />

              <Route
                path="logs"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading Logs..." />}>
                    <Logs />
                  </Suspense>
                }
              />

              <Route
                path="settings"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading Settings..." />}>
                    <Settings />
                  </Suspense>
                }
              />

              <Route
                path="support"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading Support Center..." />}>
                    <Support />
                  </Suspense>
                }
              />


              <Route
                path="branches"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading Branches..." />}>
                    <Branches />
                  </Suspense>
                }
              />

              <Route
                path="tenants"
                element={<Navigate to="/super-admin-console" replace />}
              />

              <Route
                path="access-tokens"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading Access Tokens..." />}>
                    <AccessTokens />
                  </Suspense>
                }
              />

              <Route
                path="super-admin-console"
                element={
                  user?.role === 'super_admin' ? (
                    <Suspense fallback={<LoadingFallback message="Loading Super Admin Console..." />}>
                      <SuperAdminConsole />
                    </Suspense>
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />

              {/*  Root route */}
              <Route
                index
                element={
                  isAuthenticated ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/*  Dynamic branch-configured pages (e.g. /conference-rooms, /pool-area) */}
              <Route
                path=":pageKey"
                element={
                  <Suspense fallback={<LoadingFallback message="Loading page..." />}>
                    <DynamicSlotsPage />
                  </Suspense>
                }
              />
            </Route>

            {/*  Catch all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>

      {/*  Toast Notifications */}
      <Toaster position="top-right" reverseOrder={false} />

      {/*  React Query DevTools */}
      {import.meta.env.DEV && ReactQueryDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
      )}
    </ThemeProvider>
  );
}

//  Main App Component
const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

function App() {
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
      <AppContent />
    </PersistQueryClientProvider>
  );
}

if (import.meta.env.DEV) {
  debugLog(' APP.JSX v6.3.0 - Dynamic Configurable Pages support added');
  debugLog(' Dynamic pages route: /:pageKey -> DynamicSlotsPage');
  debugLog(' Available routes: Dashboard, Rooms, Devices, Vouchers, Users, Logs, Settings, Privacy, Branches + Dynamic Pages');
}

export default App;
